/**
 * Image utility functions for handling image paths and URLs
 */

import { getCachedImageUrl } from './imageCache';

import config from '../config/env.config';

// Get the base API URL from environment or default
const API_BASE_URL = config.API_URL;

/**
 * Build full image URL from relative path with caching
 * @param {string} imagePath - Relative path to image or full URL
 * @returns {string} Full URL to image or placeholder if path is invalid
 */
export const buildImageUrl = (imagePath) => {
  return getCachedImageUrl(imagePath, () => {
    if (!imagePath) {
      return getPlaceholderImage();
    }

    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // If it's a relative path starting with /, remove it
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;

    // Build full URL - handle QR codes separately
    if (cleanPath.startsWith('images/qr_codes/')) {
      return `${API_BASE_URL}/static/${cleanPath}`;
    }
    
    return `${API_BASE_URL}/static/images/${cleanPath}`;
  });
};

/**
 * Get placeholder image URL
 * @param {string} type - Type of placeholder ('restaurant_logo', 'restaurant_cover', 'category', 'menu_item')
 * @returns {string} Placeholder image URL
 */
export const getPlaceholderImage = (type = 'default') => {
  return getCachedImageUrl(`placeholder_${type}`, () => {
    const placeholders = {
      restaurant_logo: `${API_BASE_URL}/static/images/defaults/restaurant-logo.png`,
      restaurant_cover: `${API_BASE_URL}/static/images/defaults/restaurant-cover.jpg`,
      category: `${API_BASE_URL}/static/images/defaults/category.jpg`,
      menu_item: `${API_BASE_URL}/static/images/defaults/menu-item.jpg`,
      user_avatar: `${API_BASE_URL}/static/images/defaults/user-avatar.png`,
      default: `${API_BASE_URL}/static/images/defaults/placeholder.png`
    };

    return placeholders[type] || placeholders.default;
  });
};

/**
 * Handle image loading errors by setting a placeholder
 * @param {Event} event - Image error event
 * @param {string} type - Type of placeholder to use
 */
export const handleImageError = (event, type = 'default') => {
  event.target.src = getPlaceholderImage(type);
  event.target.onerror = null; // Prevent infinite loop
};

/**
 * Validate if a path looks like a valid image path
 * @param {string} path - Path to validate
 * @returns {boolean} True if path looks valid
 */
export const isValidImagePath = (path) => {
  if (!path) return false;
  
  // Allow external URLs
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return true;
  }

  // Check for valid file extensions
  const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  return validExtensions.some(ext => path.toLowerCase().endsWith(ext));
};

/**
 * Get image component props with error handling
 * @param {string} imagePath - Path to image
 * @param {string} type - Type for placeholder
 * @param {string} alt - Alt text
 * @returns {object} Props object for img component
 */
export const getImageProps = (imagePath, type = 'default', alt = '') => {
  return {
    src: buildImageUrl(imagePath),
    alt: alt,
    onError: (e) => handleImageError(e, type),
    loading: 'lazy'
  };
};
